import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatDateTime, STAGE_DISPLAY_NAMES } from '../lib/utils';
import { 
  MessageSquare, 
  Phone, 
  Users, 
  Mail, 
  FileText,
  TrendingUp,
  Calendar,
  CheckCircle,
  Eye,
  MousePointer,
  ChevronDown,
  Loader2,
  Filter,
  Zap,
  Layers,
  Send,
  Link2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const ACTIVITY_ICONS = {
  note: MessageSquare,
  call: Phone,
  meeting: Users,
  email: Mail,
  email_sent: Mail,
  stage_change: TrendingUp,
  created: FileText,
  deck_delivered: FileText,
  followup_set: Calendar,
  build_and_send_initiated: Zap,
};

const ACTIVITY_COLORS = {
  note: 'bg-blue-100 text-blue-700 border-blue-200',
  call: 'bg-green-100 text-green-700 border-green-200',
  meeting: 'bg-purple-100 text-purple-700 border-purple-200',
  email: 'bg-orange-100 text-orange-700 border-orange-200',
  email_sent: 'bg-orange-100 text-orange-700 border-orange-200',
  stage_change: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  created: 'bg-gray-100 text-gray-700 border-gray-200',
  deck_delivered: 'bg-amber-100 text-amber-700 border-amber-200',
  followup_set: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  build_and_send_initiated: 'bg-amber-50 text-amber-800 border-amber-200',
};

const ACTIVITY_LABELS = {
  note: 'Note',
  call: 'Call',
  meeting: 'Meeting',
  email: 'Email',
  email_sent: 'Email Sent',
  stage_change: 'Stage Change',
  created: 'Created',
  deck_delivered: 'Deck Delivered',
  followup_set: 'Follow-up Set',
  build_and_send_initiated: 'Build & Send',
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'All Activities' },
  { id: 'email_sent', label: 'Emails' },
  { id: 'stage_change', label: 'Stage Changes' },
  { id: 'deck_delivered', label: 'Deck Deliveries' },
  { id: 'build_and_send_initiated', label: 'Build & Send' },
  { id: 'note', label: 'Notes' },
];

// Document 2 · Phase 6.3 — Build & Send Activity Card.
// Read-only surface that surfaces deck status + dual-tone email send state on
// the Opportunity timeline. No mutations from here — all actions navigate.

// Document 2 · Phase 6.3.1 — Time-to-send pill helpers.
// Pure derived from workflow created_at vs earliest email sent timestamp.
// Returns null when no email sent (so the pill never renders on Draft cards).
function _formatTimeToSend(createdAt, emailASentAt, emailBSentAt) {
  if (!createdAt) return null;
  if (!emailASentAt && !emailBSentAt) return null;

  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;

  const sentTimestamps = [emailASentAt, emailBSentAt]
    .filter(Boolean)
    .map((s) => new Date(s))
    .filter((d) => !Number.isNaN(d.getTime()));
  if (sentTimestamps.length === 0) return null;

  const earliest = new Date(Math.min(...sentTimestamps.map((d) => d.getTime())));

  const onlyA = !!emailASentAt && !emailBSentAt;
  const onlyB = !!emailBSentAt && !emailASentAt;
  const subject = onlyA ? 'Email A sent' : onlyB ? 'Email B sent' : 'Sent';

  const diffMs = earliest.getTime() - created.getTime();
  if (diffMs < 0) return `${subject} immediately`;

  const diffMin = Math.round(diffMs / 60000);
  const diffHr = Math.round(diffMs / 3600000);
  const sameCalendarDay = earliest.toDateString() === created.toDateString();

  if (diffMin < 60) {
    return sameCalendarDay
      ? `${subject} within the hour`
      : `${subject} ${diffMin}m later`;
  }
  if (sameCalendarDay) {
    return diffHr === 0
      ? `${subject} same day`
      : `${subject} ${diffHr}h after draft created`;
  }
  const startOfCreated = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const startOfEarliest = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
  const dayDiff = Math.round((startOfEarliest - startOfCreated) / 86400000);
  if (dayDiff <= 0) return `${subject} ${diffHr}h after draft created`;
  if (dayDiff === 1) return `${subject} next day`;
  return `${subject} ${dayDiff} days later`;
}

function BuildAndSendCard({ meta, createdAt }) {
  const sendStatus = meta?.send_status || 'draft';

  const STATUS_TOKENS = {
    draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    email_a_sent: { label: 'Email A sent', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
    email_b_sent: { label: 'Email B sent', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
    both_sent: { label: 'Both sent', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  };
  const statusToken = STATUS_TOKENS[sendStatus] || STATUS_TOKENS.draft;

  const deckStatus = meta?.deck_status || 'draft';
  const deckIsPublished = deckStatus === 'active' || deckStatus === 'published';
  const deckToken = deckIsPublished
    ? { label: 'Published', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
    : { label: 'Draft', cls: 'bg-amber-100 text-amber-800 border-amber-300' };

  // Phase 6.3.1 — Only renders when send_status !== 'draft'.
  const timeToSendLabel = _formatTimeToSend(
    createdAt,
    meta?.email_a_sent_at,
    meta?.email_b_sent_at
  );

  const renderToneRow = (version, sentAt, sentTo, key) => {
    if (!version) return null;
    const isA = (version.tone || '').toLowerCase() === 'direct_confident';
    const dotCls = isA ? 'bg-stone-500' : 'bg-amber-600';
    const toneLabel = isA ? 'Direct & Confident' : 'Warm & Consultative';
    return (
      <div className="flex items-start gap-2 text-xs" data-testid={`bs-activity-version-${key}-row`}>
        <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${dotCls}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-stone-800 truncate" data-testid={`bs-activity-version-${key}-subject`}>
            {version.subject || <span className="italic text-stone-400">(no subject)</span>}
          </p>
          <p className="text-[11px] uppercase tracking-wide font-semibold text-stone-500 mt-0.5">
            {toneLabel}
            {sentAt ? (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 normal-case tracking-normal font-normal">
                <CheckCircle className="h-3 w-3" />
                Sent{sentTo ? ` · ${sentTo}` : ''}
              </span>
            ) : (
              <span className="ml-2 normal-case tracking-normal font-normal text-stone-500">· Draft</span>
            )}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      className="mt-3 rounded-sm border border-amber-200 bg-amber-50/40 p-3 space-y-3"
      data-testid="bs-activity-card"
    >
      {/* Status row */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={`text-[10px] uppercase tracking-wider font-semibold rounded-sm ${statusToken.cls}`}
          data-testid="bs-activity-send-status"
        >
          <Send className="h-2.5 w-2.5 mr-1" />
          {statusToken.label}
        </Badge>
        <Badge
          variant="outline"
          className={`text-[10px] uppercase tracking-wider font-semibold rounded-sm ${deckToken.cls}`}
          data-testid="bs-activity-deck-status"
        >
          <Layers className="h-2.5 w-2.5 mr-1" />
          Deck · {deckToken.label}
        </Badge>
        {!deckIsPublished && (
          <span className="inline-flex items-center gap-1 text-[10px] text-stone-500 italic">
            <Link2 className="h-2.5 w-2.5" />
            Public URL activates on publish
          </span>
        )}
        {timeToSendLabel && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-emerald-800"
            data-testid="bs-activity-time-to-send"
          >
            <CheckCircle className="h-2.5 w-2.5" />
            {timeToSendLabel}
          </span>
        )}
      </div>

      {/* Email tone rows */}
      <div className="space-y-2">
        {renderToneRow(meta.version_a, meta.email_a_sent_at, meta.email_a_sent_to, 'a')}
        {renderToneRow(meta.version_b, meta.email_b_sent_at, meta.email_b_sent_to, 'b')}
      </div>

      {/* Action links (read-only navigation) */}
      <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-amber-100">
        {meta.deck_edit_url && (
          <a
            href={meta.deck_edit_url}
            className="text-[11px] font-semibold text-amber-800 hover:text-amber-900 inline-flex items-center gap-1"
            data-testid="bs-activity-deck-link"
          >
            <Layers className="h-3 w-3" />
            Open deck in editor
          </a>
        )}
        {meta.workflow_review_url && (
          <a
            href={meta.workflow_review_url}
            className="text-[11px] font-semibold text-amber-800 hover:text-amber-900 inline-flex items-center gap-1"
            data-testid="bs-activity-review-link"
          >
            <Zap className="h-3 w-3" />
            Review workflow
          </a>
        )}
      </div>
    </div>
  );
}

export default function OpportunityTimeline({ opportunityId }) {
  const { get, loading } = useApi();
  
  const [activities, setActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);

  const fetchTimeline = useCallback(async (reset = false) => {
    try {
      const currentSkip = reset ? 0 : skip;
      const params = new URLSearchParams();
      params.append('limit', '25');
      params.append('skip', currentSkip.toString());
      
      const data = await get(`/opportunities/${opportunityId}/timeline?${params.toString()}`);
      
      let filteredActivities = data.activities;
      if (filter !== 'all') {
        filteredActivities = data.activities.filter(a => a.type === filter);
      }
      
      if (reset) {
        setActivities(filteredActivities);
        setSkip(25);
      } else {
        setActivities(prev => [...prev, ...filteredActivities]);
        setSkip(prev => prev + 25);
      }
      setTotalCount(data.total_count);
      setHasMore(data.has_more);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    }
  }, [opportunityId, filter, skip, get]);

  useEffect(() => {
    fetchTimeline(true);
  }, [opportunityId, filter]);

  const loadMore = async () => {
    setLoadingMore(true);
    await fetchTimeline(false);
    setLoadingMore(false);
  };

  const renderActivityIcon = (type) => {
    const IconComponent = ACTIVITY_ICONS[type] || MessageSquare;
    return <IconComponent className="h-4 w-4" />;
  };

  const renderActivityCard = (activity, idx) => {
    const colorClass = ACTIVITY_COLORS[activity.type] || 'bg-gray-100 text-gray-700 border-gray-200';
    
    return (
      <div 
        key={activity.id || idx} 
        className="flex gap-4 relative"
        data-testid={`opp-activity-${activity.id}`}
      >
        {/* Timeline line */}
        {idx < activities.length - 1 && (
          <div className="absolute left-5 top-10 bottom-0 w-px bg-border" />
        )}
        
        {/* Icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border ${colorClass} z-10`}>
          {renderActivityIcon(activity.type)}
        </div>
        
        {/* Content */}
        <div className="flex-1 pb-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className={`text-xs rounded-sm ${colorClass}`}>
                  {ACTIVITY_LABELS[activity.type] || activity.type}
                </Badge>
              </div>
              <p className="text-sm">{activity.content}</p>
              
              {/* Email metadata */}
              {activity.type === 'email_sent' && activity.metadata && (
                <div className="flex items-center gap-3 mt-2 text-xs">
                  {activity.metadata.opened && (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <Eye className="h-3 w-3" />
                      Opened
                    </span>
                  )}
                  {activity.metadata.clicked && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <MousePointer className="h-3 w-3" />
                      Clicked
                    </span>
                  )}
                </div>
              )}
              
              {/* Deck metadata */}
              {activity.type === 'deck_delivered' && activity.metadata && (
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <Badge variant="secondary" className="rounded-sm">
                    {activity.metadata.status || 'Delivered'}
                  </Badge>
                  {activity.metadata.deck_url && (
                    <a 
                      href={activity.metadata.deck_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      View Deck
                    </a>
                  )}
                </div>
              )}

              {/* Document 2 · Phase 6.3 — Build & Send Activity Card */}
              {activity.type === 'build_and_send_initiated' && activity.metadata && (
                <BuildAndSendCard meta={activity.metadata} createdAt={activity.created_at} />
              )}
            </div>
            
            <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
              <p>{formatDateTime(activity.created_at)}</p>
              <p className="mt-0.5">{activity.created_by_name}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="rounded-sm border" data-testid="opportunity-timeline">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Activity Timeline ({totalCount})
          </CardTitle>
          
          {/* Filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] rounded-sm" data-testid="opp-timeline-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No activities yet</p>
          </div>
        ) : (
          <div className="space-y-0">
            {activities.map((activity, idx) => renderActivityCard(activity, idx))}
            
            {/* Load More */}
            {hasMore && (
              <div className="pt-4 text-center">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="rounded-sm"
                  data-testid="opp-load-more-btn"
                >
                  {loadingMore ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  )}
                  Load More
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
